/**
 * API: panel administrativo (staff-only).
 *
 * Cubre dashboard (Sprint 9.4), staff management (Sprint 11.2),
 * analytics (Sprint 12.2), chat admin assistant (Sprint 12.3) y
 * auditoría del Asistente IA en Expedientes (Sprint 17.4).
 *
 * Convención: todos los endpoints viven bajo `/admin/*` y exigen
 * `users.is_staff = TRUE` (validado en el backend).
 */

import { apiErrorFromResponse, safeFetch } from "../errors";
import { supabase } from "../supabase";
import { API_V1, getAccessToken, request } from "./_client";

// --- Dashboard (Sprint 9.4) ---

export interface TenantOverviewDTO {
  tenant_id: string;
  dane_code: string;
  display_name: string;
  municipality_name: string;
  department_code: string;
  category: string;
  onboarded_at: string | null;
  favorites_count: number;
  draft_projects_count: number;
  last_member_login_at: string | null;
  /**
   * Estado operacional (#884). Opcional a proposito: el frontend publica al
   * mezclar y `web` despliega despues, asi que una fila puede llegar sin el
   * campo; en ese caso no se marca nada.
   */
  tenant_status?: TenantStatus;
}

export interface SourceHealthDTO {
  source_code: string;
  source_name: string;
  active_opportunities: number;
}

// Los widgets de scraper (recent_scraper_runs + ultimo run por fuente) se
// retiraron del dashboard viejo: leian la tabla muerta `scraper_runs`. La
// observabilidad de corridas vive ahora en AdminScrapers (`/admin/scrapers`,
// ver TaskRunDTO mas abajo).

export interface AdminDashboardDTO {
  tenants: TenantOverviewDTO[];
  sources_health: SourceHealthDTO[];
  total_active_tenants: number;
  total_active_opportunities: number;
  total_open_alerts: number;
}

export function fetchAdminDashboard(): Promise<AdminDashboardDTO> {
  return request<AdminDashboardDTO>("/admin/dashboard");
}

// --- Staff management (Sprint 11.2) ---

export interface StaffMemberDTO {
  user_id: string;
  email: string | null;
  full_name: string;
  staff_granted_at: string | null;
  staff_granted_by_email: string | null;
}

/** Lista todos los users con `is_staff = TRUE`. */
export function fetchStaffMembers(): Promise<StaffMemberDTO[]> {
  return request<StaffMemberDTO[]>("/admin/staff");
}

/** Promueve a un usuario por email. Idempotente: si ya era staff, no falla. */
export function grantStaff(email: string): Promise<StaffMemberDTO> {
  return request<StaffMemberDTO>("/admin/grant-staff", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

/** Degrada a un staff existente. Backend bloquea self-revoke con 400. */
export function revokeStaff(userId: string): Promise<StaffMemberDTO> {
  return request<StaffMemberDTO>("/admin/revoke-staff", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId }),
  });
}

// --- Analytics summary (Sprint 12.2) ---

export interface WeeklySignupPointDTO {
  /** ISO date del lunes de la semana. */
  week_start: string;
  signups: number;
}

export interface ActiveUsersDTO {
  dau: number;
  wau: number;
  mau: number;
}

export interface TopEventRowDTO {
  event_name: string;
  count: number;
}

export interface RecentEventDTO {
  id: number;
  tenant_id: string | null;
  user_id: string | null;
  session_id: string | null;
  event_name: string;
  properties: Record<string, unknown>;
  created_at: string;
}

export interface AnalyticsSummaryDTO {
  weekly_signups: WeeklySignupPointDTO[];
  active_users: ActiveUsersDTO;
  top_events: TopEventRowDTO[];
  recent_events: RecentEventDTO[];
}

/** KPIs derivados de `analytics_events` para `/admin/analytics`. */
export function fetchAnalyticsSummary(): Promise<AnalyticsSummaryDTO> {
  return request<AnalyticsSummaryDTO>("/admin/analytics/summary");
}

// --- Chat del asistente admin (Sprint 12.3) ---

export interface ChatMessageDTO {
  role: "user" | "assistant";
  content: string;
}

export interface ChatToolCallDTO {
  name: string;
  input: Record<string, unknown>;
  is_error: boolean;
}

export interface ChatResponseDTO {
  text: string;
  tool_calls: ChatToolCallDTO[];
  model: string;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number;
  truncated: boolean;
}

/** Envía la conversación al asistente admin (Haiku 4.5). */
export function postAdminChat(messages: ChatMessageDTO[]): Promise<ChatResponseDTO> {
  return request<ChatResponseDTO>("/admin/assistant/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages }),
  });
}

// --- Audit log del Asistente IA en Expedientes (Sprint 17.4) ---

export interface AssistantAuditSummaryDTO {
  id: string;
  tenant_id: string;
  user_id: string;
  project_id: string;
  tenant_dane_code: string | null;
  tenant_municipality_name: string | null;
  user_email: string | null;
  project_name: string | null;
  result_text_preview: string | null;
  tool_calls_count: number;
  tool_calls_has_errors: boolean;
  model: string;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number | null;
  truncated: boolean;
  error: string | null;
  rating: number | null;
  rating_feedback: string | null;
  rating_at: string | null;
  created_at: string;
}

export interface AssistantAuditListResponseDTO {
  rows: AssistantAuditSummaryDTO[];
  total: number;
  limit: number;
  offset: number;
}

export interface AssistantAuditDetailDTO {
  id: string;
  tenant_id: string;
  user_id: string;
  project_id: string;
  tenant_dane_code: string | null;
  tenant_municipality_name: string | null;
  user_email: string | null;
  project_name: string | null;
  messages: { role: string; content: string }[];
  result_text: string | null;
  tool_calls: {
    name: string;
    input?: Record<string, unknown>;
    is_error: boolean;
    output_preview?: string | null;
  }[];
  model: string;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number | null;
  truncated: boolean;
  error: string | null;
  rating: number | null;
  rating_feedback: string | null;
  rating_at: string | null;
  created_at: string;
}

export interface AssistantAuditFilters {
  limit?: number;
  offset?: number;
  dane_code?: string;
  /** 1 / -1 / 0 (sentinel: sin rating). */
  rating?: 1 | -1 | 0;
  only_errors?: boolean;
  only_truncated?: boolean;
  project_id?: string;
  tenant_id?: string;
}

export function fetchAssistantAuditList(
  filters: AssistantAuditFilters = {},
): Promise<AssistantAuditListResponseDTO> {
  const qs = new URLSearchParams();
  if (filters.limit !== undefined) qs.set("limit", String(filters.limit));
  if (filters.offset !== undefined) qs.set("offset", String(filters.offset));
  if (filters.dane_code) qs.set("dane_code", filters.dane_code);
  if (filters.rating !== undefined) qs.set("rating", String(filters.rating));
  if (filters.only_errors) qs.set("only_errors", "true");
  if (filters.only_truncated) qs.set("only_truncated", "true");
  if (filters.project_id) qs.set("project_id", filters.project_id);
  if (filters.tenant_id) qs.set("tenant_id", filters.tenant_id);
  const queryString = qs.toString();
  return request<AssistantAuditListResponseDTO>(
    `/admin/assistant-audit${queryString ? `?${queryString}` : ""}`,
  );
}

export function fetchAssistantAuditDetail(auditId: string): Promise<AssistantAuditDetailDTO> {
  return request<AssistantAuditDetailDTO>(`/admin/assistant-audit/${encodeURIComponent(auditId)}`);
}

// --- Freshness de indicadores / fuentes (Sprint 20.1 — Data Foundation
// Sub-sprint 2 backend) ---

/**
 * Estado de freshness por indicador.
 *
 * - `fresh` — dentro de `update_frequency × 1.5`.
 * - `stale` — pasó la ventana saludable.
 * - `failed` — último crawler falló.
 * - `missing` — sin archivo en storage todavía.
 * - `static` — indicador que no se actualiza nunca (`update_frequency = static`).
 * - `unavailable` — `target_status` es `broken` o `pending_investigation`.
 */
export type IndicatorFreshnessStatus =
  "fresh" | "stale" | "missing" | "failed" | "static" | "unavailable";

export interface IndicatorFreshnessDTO {
  indicator_key: string;
  label: string;
  group: string;
  /** "active" | "pending_investigation" | "broken" — del registry interno. */
  target_status: string;
  /** "csv" | "pdf" | "soda" | "municipality_field_csv" | "derived". */
  source_type: string;
  /** "anual" | "trimestral" | "mensual" | "semanal" | "static". */
  update_frequency: string;
  storage_folder: string | null;
  storage_filename: string | null;
  storage_uploaded_at: string | null;
  last_run_at: string | null;
  /** "success" | "failed" | null. */
  last_run_status: string | null;
  last_run_rows_upserted: number | null;
  freshness_status: IndicatorFreshnessStatus;
  age_days: number | null;
}

export interface IndicatorFreshnessResponse {
  generated_at: string;
  total: number;
  /** Counts por status. Keys posibles: fresh/stale/missing/failed/static/unavailable. */
  summary: Record<string, number>;
  items: IndicatorFreshnessDTO[];
}

/** Snapshot de freshness por indicador. Solo staff. */
export function fetchAdminFreshness(): Promise<IndicatorFreshnessResponse> {
  return request<IndicatorFreshnessResponse>("/admin/indicators/freshness");
}

// --- Provisioning (Sprint 13.5, expuesto en frontend en Sub-sprint 20.2) ---

export interface ProvisionTenantResponse {
  user_id: string;
  email: string;
  tenant_id: string;
  dane_code: string;
  municipality_name: string | null;
  created_now: boolean;
}

/** Asigna (o crea) un tenant para un usuario por email + DANE. Solo staff. */
export function provisionTenant(daneCode: string, email: string): Promise<ProvisionTenantResponse> {
  return request<ProvisionTenantResponse>("/admin/provision-tenant", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dane_code: daneCode, email }),
  });
}

// --- Tenants management (Sub-sprint 20.2) ---

/**
 * Resumen de un tenant para el listado del admin panel.
 *
 * `members_count` y `last_login_at` se calculan sobre memberships
 * activas (no revocadas). `tier` es esencial|pro (default esencial
 * para tenants viejos sin tier seteado). `subscription_state` es el
 * enum de Postgres serializado como texto.
 */
export type TenantStatus = "active" | "inactive" | "pending_deletion";

export interface AdminTenantSummaryDTO {
  id: string;
  dane_code: string;
  display_name: string;
  municipality_name: string;
  department_name: string;
  tier: string;
  /** Estado **comercial** — dónde va la plata. Ocho valores, el embudo completo. */
  subscription_state: string;
  /**
   * Estado **operacional** — si el sistema le responde hoy. Es un eje distinto
   * del comercial: un muni en mora puede quedar `cancelled` en billing y
   * `active` en operación durante un grace period (decisión 23-7a).
   */
  tenant_status: TenantStatus;
  /** Solo cuando `tenant_status === "pending_deletion"`. */
  deletion_scheduled_at: string | null;
  onboarded_at: string | null;
  created_at: string;
  /**
   * Asientos ocupados: memberships no revocadas y sin el staff de Kaptar,
   * la misma cuenta del cupo al invitar (#579).
   */
  members_count: number;
  /** Cupo del plan: "2 de 3". */
  seats_limit: number;
  last_login_at: string | null;
}

export interface AdminTenantsListResponse {
  total: number;
  items: AdminTenantSummaryDTO[];
}

export interface AdminTenantsFilters {
  /** Búsqueda libre en display_name, dane_code y nombre del municipio. */
  q?: string;
  tier?: string;
  subscription_state?: string;
  /** Estado operacional. Independiente del comercial. */
  tenant_status?: TenantStatus;
}

/** Lista paginada de tenants. Solo staff. */
export function fetchAdminTenants(
  filters: AdminTenantsFilters = {},
): Promise<AdminTenantsListResponse> {
  const qs = new URLSearchParams();
  if (filters.q) qs.set("q", filters.q);
  if (filters.tier) qs.set("tier", filters.tier);
  if (filters.subscription_state) qs.set("subscription_state", filters.subscription_state);
  if (filters.tenant_status) qs.set("tenant_status", filters.tenant_status);
  const query = qs.toString();
  return request<AdminTenantsListResponse>(`/admin/tenants${query ? `?${query}` : ""}`);
}

/**
 * Un miembro de un tenant — incluye estado pending/active/revoked.
 *
 * - `accepted_at = null` → invitación pendiente (magic link no usado aún).
 * - `is_revoked = true` → ex-miembro (soft delete con timestamp).
 * - `last_login_at` viene de `auth.users.last_sign_in_at`.
 */
export interface AdminMemberDTO {
  membership_id: string;
  user_id: string;
  full_name: string;
  email: string;
  role: string;
  invited_by_email: string | null;
  invited_at: string;
  accepted_at: string | null;
  last_login_at: string | null;
  is_revoked: boolean;
  /** Staff de Kaptar que entra a dar soporte: se lista, pero no ocupa asiento. */
  is_staff: boolean;
}

export interface AdminMembersListResponse {
  total: number;
  items: AdminMemberDTO[];
}

/** Miembros del tenant — incluye revocados. Solo staff. */
export function fetchAdminTenantMembers(tenantId: string): Promise<AdminMembersListResponse> {
  return request<AdminMembersListResponse>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/members`,
  );
}

export type AdminMemberRole = "admin_municipal" | "formulador" | "colaborador";

/**
 * Invita a un miembro nuevo via magic link de Supabase.
 *
 * El backend llama `auth.admin.invite_user_by_email`, hace UPSERT en
 * `public.users` y crea la membership en estado `accepted_at = NULL`.
 * El invitado completa el flow desde el email.
 */
/**
 * Pausa o reactiva un tenant (Sprint 21-2).
 *
 * `inactive` corta el servicio: sin Radar, sin alertas y sin login. Los datos
 * quedan intactos. Volver a `active` **cancela un borrado programado** si lo
 * había — el backend lo hace en el mismo statement.
 *
 * No acepta `pending_deletion`: eso necesita fecha y tiene endpoint propio.
 */
export function changeAdminTenantStatus(
  tenantId: string,
  tenantStatus: "active" | "inactive",
  reason?: string,
): Promise<void> {
  return request<void>(`/admin/tenants/${encodeURIComponent(tenantId)}/status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tenant_status: tenantStatus, reason: reason || null }),
  });
}

export interface ScheduledDeletionDTO {
  tenant_id: string;
  display_name: string;
  deletion_scheduled_at: string;
  grace_period_days: number;
}

/**
 * Programa el borrado definitivo (Sprint 21-4).
 *
 * `confirmDisplayName` tiene que coincidir exacto con el nombre del tenant —
 * el backend devuelve 409 si no. Es lo que obliga a leer qué se está borrando.
 */
export function scheduleAdminTenantDeletion(
  tenantId: string,
  confirmDisplayName: string,
  gracePeriodDays = 30,
  reason?: string,
): Promise<ScheduledDeletionDTO> {
  return request<ScheduledDeletionDTO>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/schedule-deletion`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        confirm_display_name: confirmDisplayName,
        grace_period_days: gracePeriodDays,
        reason: reason || null,
      }),
    },
  );
}

export function inviteAdminTenantMember(
  tenantId: string,
  email: string,
  role: AdminMemberRole,
): Promise<AdminMemberDTO> {
  return request<AdminMemberDTO>(`/admin/tenants/${encodeURIComponent(tenantId)}/invite-member`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, role }),
  });
}

/**
 * Revoca una membership (soft delete). Backend bloquea self-revoke con 400.
 * Idempotente: si ya estaba revocada, sigue siendo 204.
 *
 * El endpoint devuelve 204 sin body — no podemos usar `request<T>()` que
 * asume JSON. Reimplementamos el wrapper auth/error acá para DELETE.
 */
export async function revokeAdminTenantMember(
  tenantId: string,
  membershipId: string,
): Promise<void> {
  const token = await getAccessToken();
  const headers = new Headers({ Accept: "application/json" });
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await safeFetch(
    `${API_V1}/admin/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(membershipId)}`,
    { method: "DELETE", headers },
  );

  if (res.status === 401) {
    await supabase.auth.signOut();
  }
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }
  // 204 No Content — nada que parsear.
}

// --- Scrapers (Sub-sprint 20.3) ---

/**
 * Una fila del listado de scrapers — el ultimo run + proximo schedule.
 *
 * `is_running = true` cuando hay alguna corrida en estado `running` para
 * ese task. `last_run_status` puede ser `running` / `success` / `failed`.
 * `next_run_at` y `schedule_human` son null si el task NO tiene entry en
 * el beat schedule (solo se dispara manualmente).
 */
export interface ScraperTaskDTO {
  task_name: string;
  display_name: string;
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_rows: number | null;
  last_run_duration_seconds: number | null;
  next_run_at: string | null;
  schedule_human: string | null;
  triggered_by: string | null;
  is_running: boolean;
  /**
   * Alarma por racha (issue #809). Opcionales: llegan desde #927 y son
   * `null` en los tasks que no se vigilan.
   */
  last_run_items_listados?: number | null;
  racha_listado_en_cero?: number | null;
  umbral_listado_en_cero?: number | null;
}

export interface ScrapersListResponse {
  generated_at: string;
  items: ScraperTaskDTO[];
}

/** Lista los scrapers tracked con su ultimo run + proximo schedule. Solo staff. */
export function fetchAdminScrapers(): Promise<ScrapersListResponse> {
  return request<ScrapersListResponse>("/admin/scrapers");
}

/** Un run individual del task — usado en el historial. */
export interface TaskRunDTO {
  id: number;
  task_name: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  rows_ingested: number | null;
  error_message: string | null;
  triggered_by: string;
  triggered_by_user_id: string | null;
  duration_seconds: number | null;
}

export interface TaskRunsResponse {
  items: TaskRunDTO[];
}

/** Historial (ultimos N runs) del task. Default 20, max 100. */
export function fetchAdminScraperRuns(taskName: string, limit = 20): Promise<TaskRunsResponse> {
  const qs = new URLSearchParams({ limit: String(limit) });
  return request<TaskRunsResponse>(
    `/admin/scrapers/${encodeURIComponent(taskName)}/runs?${qs.toString()}`,
  );
}

export interface TriggerScraperResponse {
  task_id: string;
  task_name: string;
  status: string;
}

/**
 * Encola el task manualmente. El backend pasa `triggered_by='manual'` y
 * el user_id del staff via headers Celery, que el signal handler lee y
 * persiste en `ingestion_task_runs`. Whitelist enforced backend-side.
 */
export function triggerAdminScraper(taskName: string): Promise<TriggerScraperResponse> {
  return request<TriggerScraperResponse>(
    `/admin/scrapers/${encodeURIComponent(taskName)}/trigger`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
  );
}

// --- Convocatorias curatorial (Sub-sprint 20.4) ---

/**
 * Resumen de una convocatoria para el listado curatorial.
 *
 * `has_edits = true` cuando ya tiene al menos 1 row en
 * `opportunity_admin_edits`. Util para mostrar un badge en la tabla.
 * Los montos vienen como string (Decimal serializado) porque NUMERIC(18,2)
 * no encaja en `number` JS sin perder precision en cifras grandes.
 */
export interface AdminOpportunitySummaryDTO {
  id: string;
  source_code: string;
  source_name: string;
  title: string;
  sector: string | null;
  amount_total_cop: string | null;
  amount_per_project_max_cop: string | null;
  closes_at: string | null;
  /** "Hasta agotar cupos" (#692): sin fecha de cierre por diseño. */
  until_quota_exhausted?: boolean;
  status: string;
  has_edits: boolean;
  ingested_at: string;
  updated_at: string;
}

export interface AdminOpportunitiesListResponse {
  total: number;
  /**
   * Conteo por status de todas las filas que cumplen los filtros, no solo de
   * la pagina (#723). Opcional: un backend anterior no lo manda, y entonces
   * el resumen muestra solo el total, que si es cierto.
   */
  counts_by_status?: Record<string, number>;
  items: AdminOpportunitySummaryDTO[];
}

export interface AdminOpportunityDetailDTO {
  id: string;
  source_code: string;
  source_name: string;
  external_id: string | null;
  title: string;
  description: string | null;
  sector: string | null;
  amount_total_cop: string | null;
  amount_per_project_max_cop: string | null;
  opens_at: string | null;
  closes_at: string | null;
  until_quota_exhausted?: boolean;
  source_url: string;
  status: string;
  has_edits: boolean;
  ingested_at: string;
  updated_at: string;
}

export interface AdminOpportunitiesFilters {
  q?: string;
  source_code?: string;
  sector?: string;
  status?: string;
  closes_within_days?: number;
  limit?: number;
  offset?: number;
}

/** Lista paginada de convocatorias para curacion. Solo staff. */
export function fetchAdminOpportunities(
  filters: AdminOpportunitiesFilters = {},
): Promise<AdminOpportunitiesListResponse> {
  const qs = new URLSearchParams();
  if (filters.q) qs.set("q", filters.q);
  if (filters.source_code) qs.set("source_code", filters.source_code);
  if (filters.sector) qs.set("sector", filters.sector);
  if (filters.status) qs.set("status", filters.status);
  if (filters.closes_within_days !== undefined)
    qs.set("closes_within_days", String(filters.closes_within_days));
  if (filters.limit !== undefined) qs.set("limit", String(filters.limit));
  if (filters.offset !== undefined) qs.set("offset", String(filters.offset));
  const query = qs.toString();
  return request<AdminOpportunitiesListResponse>(`/admin/opportunities${query ? `?${query}` : ""}`);
}

/**
 * Detalle completo (incluye `description` y `opens_at`, que el listado no
 * trae). El drawer curatorial se hidrata de aqui. 404 si no existe.
 */
export function fetchAdminOpportunityDetail(
  opportunityId: string,
): Promise<AdminOpportunityDetailDTO> {
  return request<AdminOpportunityDetailDTO>(
    `/admin/opportunities/${encodeURIComponent(opportunityId)}`,
  );
}

/**
 * Edit curatorial. Solo enviar campos a actualizar.
 *
 * Backend devuelve 400 si los valores enviados igualan los actuales,
 * 404 si la convocatoria no existe, 422 si llega un campo desconocido
 * (`extra='forbid'`).
 */
export interface PatchOpportunityRequest {
  title?: string;
  description?: string | null;
  sector?: string | null;
  amount_total_cop?: string | null;
  amount_per_project_max_cop?: string | null;
  opens_at?: string | null;
  closes_at?: string | null;
  notes?: string | null;
}

export function patchAdminOpportunity(
  opportunityId: string,
  payload: PatchOpportunityRequest,
): Promise<AdminOpportunityDetailDTO> {
  return request<AdminOpportunityDetailDTO>(
    `/admin/opportunities/${encodeURIComponent(opportunityId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

/** Archivar (soft-delete). 409 si ya estaba archivada. */
export function archiveAdminOpportunity(
  opportunityId: string,
  notes?: string,
): Promise<AdminOpportunityDetailDTO> {
  return request<AdminOpportunityDetailDTO>(
    `/admin/opportunities/${encodeURIComponent(opportunityId)}/archive`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes: notes ?? null }),
    },
  );
}

/** Restaura a `active`. 409 si no estaba archivada. */
export function restoreAdminOpportunity(
  opportunityId: string,
  notes?: string,
): Promise<AdminOpportunityDetailDTO> {
  return request<AdminOpportunityDetailDTO>(
    `/admin/opportunities/${encodeURIComponent(opportunityId)}/restore`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes: notes ?? null }),
    },
  );
}

export interface OpportunityEditDTO {
  id: number;
  edited_by_email: string;
  edited_at: string;
  action: string;
  /** `{ campo: { from, to } }` para edit_fields; `{}` para archive/restore. */
  field_changes: Record<string, { from: unknown; to: unknown }>;
  notes: string | null;
}

export interface OpportunityEditLogResponse {
  items: OpportunityEditDTO[];
}

/** Historial (ultimos 50) de edits curatoriales. */
export function fetchAdminOpportunityEditLog(
  opportunityId: string,
): Promise<OpportunityEditLogResponse> {
  return request<OpportunityEditLogResponse>(
    `/admin/opportunities/${encodeURIComponent(opportunityId)}/edit-log`,
  );
}
